import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslationWithFallback from 'hooks/use-translation-with-fallback';

// Utils
import bemify from 'utils/bemify';

// Components
import Button from 'components/button';
import Icon from 'components/icon';
import Input from 'components/input';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const SearchInput = ({
  className = null,
  inputClassName = null,
  onChange = () => {},
  placeholder,
  ...inputProps
}) => {
  const { t } = useTranslationWithFallback('common');

  const [searchValue, setSearchValue] = useState('');

  const inputRef = useRef(null);

  const handleInputChange = useCallback((value) => {
    setSearchValue(value);

    onChange(value);
  }, [onChange]);

  const handleClearInputButtonClick = useCallback(() => {
    handleInputChange('');
  }, [handleInputChange]);

  const clearButton = useMemo(() => {
    if (searchValue) {
      return (
        <Button
          icon="close"
          iconSize="s"
          noBackground
          theme="unstyled"
          onClick={handleClearInputButtonClick}
        />
      );
    }

    return null;
  }, [handleClearInputButtonClick, searchValue]);

  useEffect(() => {
    if (inputProps.value) {
      setSearchValue(inputProps.value);
    }
  }, [inputProps]);

  const inputIcon = (
    <Icon name="search" />
  );

  return (
    <div className={`${styles.searchInput}${className ? ` ${className}` : ''}`}>
      <Input
        ref={inputRef}
        className={styles.searchInput__inputWrapper}
        inputClassName={`${styles.searchInput__input}${inputClassName ? ` ${inputClassName}` : ''}`}
        leftSlot={inputIcon}
        placeholder={placeholder || t('search_input_placeholder')}
        rightSlot={clearButton}
        slotMarginSize="s"
        value={searchValue}
        onChange={handleInputChange}
        {...inputProps}
      />
    </div>
  );
};

SearchInput.propTypes = {
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string
};

SearchInput.displayName = 'Search Input';

export default SearchInput;
